import { useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { Box, Button, Chip, Typography } from "@mui/material";
import { baseTheme } from "../../../themes/shipinsure";

const STATUS_STYLES = {
  pending: {
    iconColor: "#6B4A00",
    chipBackground: "#FFF3D6",
    chipColor: "#5C3D00",
  },
  new: {
    iconColor: "#6B4A00",
    chipBackground: "#FFF3D6",
    chipColor: "#5C3D00",
  },
  denied: {
    iconColor: baseTheme.colors.error,
    chipBackground: "#FFF1F0",
    chipColor: "#B71C1C",
  },
  default: {
    iconColor: "#1B7F3B",
    chipBackground: "#E8F5EC",
    chipColor: "#145C2B",
  },
};

const META_TEXT_COLOR = "#1E293B";

export function StatusHero({
  hero,
  claimNumber,
  nextSteps,
  notificationEmail,
  statusAnnouncement,
  t,
}) {
  const [copied, setCopied] = useState(false);
  const isDenied = hero.key === "denied";
  const isAwaitingReview = hero.key === "pending" || hero.key === "new";
  const styles = STATUS_STYLES[hero.key] || STATUS_STYLES.default;
  const Icon = isDenied
    ? ErrorOutlineIcon
    : isAwaitingReview
      ? FactCheckOutlinedIcon
      : CheckCircleOutlineIcon;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(claimNumber);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box mb="16px" position="relative">
      <Typography
        component="p"
        sx={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
        aria-live="polite"
      >
        {statusAnnouncement}
      </Typography>

      <Box
        display="flex"
        alignItems="flex-start"
        gap="12px"
        mb={nextSteps ? "12px" : 0}
      >
        <Icon
          sx={{ fontSize: 36, color: styles.iconColor, mt: "2px" }}
          aria-hidden
        />
        <Box flex={1} minWidth={0}>
          <Typography
            variant="h4"
            fontWeight={700}
            mb="6px"
            sx={{ fontSize: { xs: "1.5rem", md: "1.75rem" } }}
          >
            {hero.title}
          </Typography>
          <Typography color={META_TEXT_COLOR} mb="10px" fontSize="15px">
            {hero.description}
          </Typography>
          <Chip
            label={t("status.statusChip", { status: hero.statusLabel })}
            sx={{
              backgroundColor: styles.chipBackground,
              color: styles.chipColor,
              fontWeight: 700,
              fontSize: "14px",
              mb: "10px",
            }}
          />
          <Box display="flex" alignItems="center" flexWrap="wrap" gap="6px">
            <Typography fontSize="14px" color={META_TEXT_COLOR}>
              {t("status.claimNumber")}:{" "}
              <Box
                component="span"
                fontWeight={600}
                color={baseTheme.colors.text}
              >
                {claimNumber}
              </Box>
            </Typography>
            <Button
              size="small"
              onClick={handleCopy}
              startIcon={<ContentCopyIcon sx={{ fontSize: "14px !important" }} />}
              aria-label={t("status.copyClaimNumberAria")}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                color: META_TEXT_COLOR,
                minWidth: 0,
                px: 1,
                py: 0.25,
              }}
            >
              {copied ? t("status.claimNumberCopied") : t("status.copyClaimNumber")}
            </Button>
          </Box>
        </Box>
      </Box>

      {nextSteps ? (
        <Box
          sx={{
            border: "1px solid #C5D0E0",
            borderRadius: "12px",
            padding: "16px",
            backgroundColor: "#F8FAFC",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            gap="8px"
            mb="8px"
          >
            <Typography fontWeight={700} fontSize="16px">
              {nextSteps.title}
            </Typography>
            {nextSteps.showNoActionBadge ? (
              <Chip
                size="small"
                label={t("status.noActionNeededBadge")}
                sx={{
                  backgroundColor: "#E8F5EC",
                  color: "#145C2B",
                  fontWeight: 700,
                  fontSize: "13px",
                }}
              />
            ) : null}
          </Box>
          {nextSteps.timeframe ? (
            <Typography fontSize="15px" color={baseTheme.colors.text} mb="6px">
              {nextSteps.timeframe}
            </Typography>
          ) : null}
          <Typography
            fontSize="15px"
            color={META_TEXT_COLOR}
            mb={notificationEmail ? "6px" : 0}
          >
            {nextSteps.body}
          </Typography>
          {notificationEmail ? (
            <Typography fontSize="14px" color={META_TEXT_COLOR}>
              {t("status.updatesSentTo", { email: notificationEmail })}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
